import React, { useEffect, useState } from "react";
import { ClipboardList, PoundSterling, ShoppingCart, Truck, Star, Boxes as BoxesIcon, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";
import { Stat } from "./ui";

const PIPELINE = [
  { key: "new_orders", label: "New / Unpaid", tab: "payments" },
  { key: "needs_purchasing", label: "Picking", tab: "fx-picking" },
  { key: "purchased", label: "Packing", tab: "fx-packing" },
  { key: "packing", label: "Containers", tab: "fx-containers" },
  { key: "shipped", label: "Inbound", tab: "fx-inbound" },
  { key: "delivered", label: "Delivery", tab: "fx-delivery" },
];

export default function DashboardHome({ onGo }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-[#5C5C5C]">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#306D29] mb-3">Today</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <Stat icon={ClipboardList} label="New orders" value={d.new_orders_today} />
          <Stat icon={PoundSterling} label="Revenue" value={`GH₵ ${d.revenue_today_ghs.toLocaleString()}`} sub={`£${d.revenue_today_gbp.toLocaleString()}`} />
          <Stat icon={ShoppingCart} label="Pending purchase" value={d.pending_purchase} tone="text-[#D9933B]" />
          <Stat icon={Truck} label="Shipping" value={d.shipping} />
          <Stat icon={Star} label="Reviews pending" value={d.reviews_pending} />
          <Stat icon={BoxesIcon} label="Active boxes" value={d.active_boxes} sub={`${d.draft_products} draft products`} />
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#306D29] mb-3">Profit across all orders</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="margin-report">
          <Stat icon={PoundSterling} label="Total revenue (paid)"
            value={`GH₵ ${Math.round((d.total_revenue_gbp || 0) * (d.fx_gbp_ghs || 19.5)).toLocaleString()}`}
            sub={`£${(d.total_revenue_gbp || 0).toLocaleString()} · ${d.orders_paid || 0} orders`} />
          <Stat icon={ShoppingCart} label="Total spent buying"
            value={`GH₵ ${Math.round((d.total_spent_gbp || 0) * (d.fx_gbp_ghs || 19.5)).toLocaleString()}`}
            sub={`£${(d.total_spent_gbp || 0).toLocaleString()}`} tone="text-[#D9933B]" />
          <Stat icon={TrendingUp} label="Overall margin"
            value={`GH₵ ${Math.round((d.margin_gbp || 0) * (d.fx_gbp_ghs || 19.5)).toLocaleString()}`}
            sub={`£${(d.margin_gbp || 0).toLocaleString()} · ${d.margin_pct || 0}% of revenue`}
            tone={(d.margin_gbp || 0) >= 0 ? "text-[#306D29]" : "text-[#B23B3B]"} />
        </div>
        <p className="text-[11px] text-[#5C5C5C] mt-2">Spent = what shoppers recorded paying at the shops. Margin = revenue (incl. handling &amp; shipping fees) minus what we spent buying.</p>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#306D29] mb-3">Live operations pipeline</p>
        <div className="flex flex-col md:flex-row gap-3 md:items-stretch">
          {PIPELINE.map((s, i) => (
            <React.Fragment key={s.key}>
              <button onClick={() => onGo(s.tab)} data-testid={`pipe-${s.key}`}
                className="flex-1 bg-white border border-black/5 rounded-2xl p-5 text-left hover:border-[#306D29]/40 hover:shadow-[0_10px_28px_rgba(13,83,14,0.08)]"
                style={{ transition: "border-color 0.2s ease, box-shadow 0.2s ease" }}>
                <p className="font-display text-4xl text-[#0D530E]">{d.pipeline[s.key]}</p>
                <p className="text-sm text-[#5C5C5C] mt-1">{s.label}</p>
              </button>
              {i < PIPELINE.length - 1 && <div className="hidden md:flex items-center text-[#306D29]/40 font-bold">→</div>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
